/**
 * Biomes.
 *
 * Kate: "You can also change the map. So if everyone agrees on it, you can change the
 * map to anything you want... You can change the map to a desert, an ocean, or plains.
 * Like oak trees, not forest, just like Plains."
 *
 * Four maps, jungle being the default. Only the terrain lives here — the animals each
 * biome unlocks (camels in the desert; dolphins, whales and fish in the ocean) are M4,
 * and the group vote that switches between them is M6.
 */
import * as THREE from 'three'

export type BiomeId = 'jungle' | 'desert' | 'ocean' | 'plains' | 'mountain'

export interface BiomeSpec {
  id: BiomeId
  name: string
  ground: number
  sky: number
  /** How many trees to grow. Kate's plains are "oak trees, not forest" — sparse. */
  treeCount: number
  /**
   * Trunk height in metres, as [shortest, extra range]. Every biome grew the same
   * 7--14m trunks, so the plains were jungle trees with gaps between them rather than
   * the oaks Kate asked for: an oak is shorter than a rainforest tree and much wider.
   */
  treeHeight: [min: number, range: number]
  trunk: number
  canopy: number
  /** Canopy radius multiplier — round oaks are broader than jungle crowns. */
  canopySpread: number
  /** Radius of the pond, or 'flooded' when the whole map is water. */
  water: number | 'flooded'
  fog: [near: number, far: number]
  /** Ground cover: how many tufts and how tall. Ocean has none. */
  undergrowth: { count: number; height: number; color: number }
}

export const BIOMES: Record<BiomeId, BiomeSpec> = {
  jungle: {
    id: 'jungle',
    name: 'Jungle',
    ground: 0x3f5c2a,
    sky: 0x8fb7d4,
    treeCount: 1500,
    treeHeight: [7, 7],
    trunk: 0x54402c,
    canopy: 0x2f5d24,
    canopySpread: 1,
    water: 9,
    fog: [30, 110],
    undergrowth: { count: 17000, height: 0.62, color: 0x3f6b2a },
  },
  desert: {
    id: 'desert',
    name: 'Desert',
    ground: 0xc9a86a,
    sky: 0xe0d3ae,
    // Not treeless — just very nearly. Kate only added camels here, nothing about plants.
    treeCount: 85,
    treeHeight: [7, 7],
    trunk: 0x8a7247,
    canopy: 0x6f7a45,
    canopySpread: 0.7,
    water: 5,
    fog: [40, 150],
    undergrowth: { count: 1400, height: 0.38, color: 0x9a8f52 },
  },
  ocean: {
    id: 'ocean',
    name: 'Ocean',
    ground: 0x2a5c72,
    sky: 0x7fb4cc,
    treeCount: 0,
    treeHeight: [7, 7],
    trunk: 0x54402c,
    canopy: 0x2f5d24,
    canopySpread: 1,
    water: 'flooded',
    fog: [25, 95],
    undergrowth: { count: 0, height: 0, color: 0x2f6b5a },
  },
  plains: {
    id: 'plains',
    name: 'Plains',
    ground: 0x6f8c3f,
    sky: 0x9dc4e0,
    // "Oak trees, not forest, just like Plains" — scattered, not a canopy.
    treeCount: 190,
    // Shorter and stouter than a jungle tree, which is what makes an oak read as an oak.
    treeHeight: [4.5, 3.5],
    trunk: 0x6b5136,
    canopy: 0x4d7a2c,
    canopySpread: 1.5,
    water: 7,
    fog: [55, 190],
    undergrowth: { count: 14000, height: 0.42, color: 0x7d9c47 },
  },
  /*
   * Kate, 2026-08-23, listing the places she cannot travel to: "jungle to plains, or
   * jungle to the mountain biome I made."
   *
   * ASSUMPTION. No recording contains her designing a mountain, so it came from a
   * conversation nobody taped. She named it as hers and as somewhere she expects to
   * go, so it exists: cold and high and bare, a few short wind-bitten trees, rock
   * underfoot, thin grass, no pond. If she describes it differently, her version wins.
   */
  mountain: {
    id: 'mountain',
    name: 'Mountain',
    ground: 0x7c7a72,
    sky: 0xb9cee0,
    treeCount: 90,
    treeHeight: [3.5, 2.5],
    trunk: 0x4f4438,
    canopy: 0x35502f,
    canopySpread: 0.8,
    water: 0,
    fog: [70, 230],
    undergrowth: { count: 5000, height: 0.24, color: 0x6f7a58 },
  },
}

export const BIOME_ORDER: BiomeId[] = ['jungle', 'desert', 'ocean', 'plains', 'mountain']

export function skyColor(id: BiomeId): THREE.Color {
  return new THREE.Color(BIOMES[id].sky)
}
